import React, { useEffect, useState, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import CsvDownloader from 'react-csv-downloader';
import { Layout, Row, Col, Select, Input, DatePicker } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

import PdButton from 'components/pd-button';
import StatusList from 'components/list-status';

import { ProfileContext } from 'contexts/profile';

import OrderService from 'services/order';
import WarehouseService from 'services/warehouse';

import { errHandling, priceFormat } from 'utils';
import addDay from 'utils/addDay';
import queryString from 'query-string';

const { Content } = Layout;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const dateFormat = 'YYYY/MM/DD';

export function Orders() {
  const location = useLocation();

  const [couriers, setCouriers] = useState([]);
  const [endDatePick, setEndDate] = useState('');
  const [loadStatus, setLoadStatus] = useState(true);
  const [loadOrderReport, setLoadOrderReport] = useState(false);
  const [nextPage, setNextPage] = useState(1);
  const [orderForDownload, setOrderForDownload] = useState([]);
  const [orderList, setOrderList] = useState([]);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [selectedCourier, setSelectedCourier] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [sortBy, setSortBy] = useState('ordered_at desc');
  const [startDatePick, setStartDate] = useState('');
  const [status, setStatus] = useState('');
  const [warehouseList, setWarehouseList] = useState([]);

  const { fetchProfile } = useContext(ProfileContext);

  const parsed = queryString.parse(location.search);
  const navigate = useNavigate();

  const customElement = (row) => (
    <div className="button-table">
      <Link to={`/orders/detail?id=${row.id}`}>
        <PdButton type="primary" style={{ height: 32 }}>
          Detail
        </PdButton>
      </Link>
    </div>
  );

  customElement.displayName = 'customButton';

  const [columns] = useState([
    {
      name: 'Order No.',
      selector: row => row.formatted_id,
      width: '150px'
    },
    {
      name: 'Invoice',
      selector: row => row.invoice_number,
      width: '220px'
    },
    {
      name: 'Date',
      selector: row => row.ordered_at,
      format: (row) => {
        return dayjs(row.ordered_at).format('DD/MM/YYYY');
      },
      width: '12%',
      center: true,
    },
    {
      name: 'Customer',
      selector: row => row.user,
      format: (row) => `${row.user.first_name} ${row.user.last_name}`,
    },
    {
      name: 'Amount',
      selector: row => row.net_price,
      format: (row) => {
        return 'IDR ' + priceFormat(row.net_price);
      },
    },
    {
      name: 'Status',
      selector: row => row.status,
      format: (row) => {
        let str = row.status.split('_');
        str = str.map((item) => {
          return item.charAt(0).toUpperCase() + item.slice(1);
        });
        return str.join(' ');
      },
      width: '10%',
      center: true,
    },
    {
      name: '',
      cell: customElement,
      center: true,
      width: '15%',
    },
  ]);

  async function fetchOrderForDownload(
    filter,
    query,
    sort,
    courier,
    startDate,
    endDate
  ) {
    setLoadOrderReport(true);
    let newEndDate = endDate;
    if (newEndDate) {
      newEndDate = addDay(endDate, 1);
    }
    setOrderForDownload([]);
    const { data } = await OrderService.getOrderReport(
      filter,
      query,
      startDate,
      newEndDate,
      sort,
      courier,
      1
    );
    let orderData = data.data;

    const removeDuplicateTotal = (orders) => {
      const result = [];
      const seenIds = {};

      for (const item of orders) {
        const { order_number, total_payment } = item;
        if (!seenIds[order_number]) {
          result.push({
            ...item,
            order_number,
            total_payment,
            warehouse: item.warehouse ? item.warehouse.title : '-',
            installation: Number(item.installation_fee) > 0 ? 'Yes' : 'No',
          });
          seenIds[order_number] = true;
        } else {
          result.push({
            ...item,
            order_number,
            total_payment: '',
            warehouse: item.warehouse ? item.warehouse.title : '-',
            installation: Number(item.installation_fee) > 0 ? 'Yes' : 'No',
          });
        }
      }

      return result;
    };
    setOrderForDownload(removeDuplicateTotal(orderData));

    setTimeout(() => {
      const btn = document.querySelector('.csv-download-trigger');
      if (btn) {
        btn.click();
      }
    }, 0);

    setLoadOrderReport(false);
  }

  async function fetchOrderList({
    courier,
    endDate,
    filter,
    page = 1,
    query,
    sort,
    startDate,
    total = 10,
    warehouse_id,
  }) {
    setLoadStatus(true);
    try {
      let newEndDate = endDate;
      if (newEndDate) {
        newEndDate = addDay(endDate, 1);
      }

      const { data } = await OrderService.getOrderList({
        courier_id: courier,
        cursor: page,
        end_date: newEndDate,
        limit: total,
        q: query,
        sort_by: sort,
        start_date: startDate,
        status: filter,
        warehouse_id,
      });
      const orderData = data.data;
      const modifiedOrderData = orderData.map((order) => {
        const { warehouse = {} } = order;
        return { ...order, warehouse };
      });
      const metadata = data.metadata;
      setNextPage(metadata.next_cursor);
      setLoadStatus(false);
      setOrderList(modifiedOrderData);
    } catch {
      setOrderList([]);
      setNextPage(1);
    } finally {
      setLoadStatus(false);
    }
  }

  async function getCourierList() {
    try {
      const { data } = await OrderService.getCourierList();
      const CourierList = data.data;
      setCouriers(CourierList);
    } catch (err) {
      errHandling(err);
    }
  }

  async function getWarehouseList() {
    try {
      const {
        data: { data = [] },
      } = await WarehouseService.getWarehouseList({ status: 'active' });
      setWarehouseList(data);
    } catch (error) {
      errHandling(error);
    }
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/orders',
      search: stringified,
    });
  }

  function onChangeDate(dates, datesString) {
    const startDate = datesString[0];
    if (startDate === '') {
      delete parsed['startDate'];
    } else {
      parsed['startDate'] = startDate;
    }

    const endDate = datesString[1];
    if (endDate === '') {
      delete parsed['endDate'];
    } else {
      parsed['endDate'] = endDate;
    }

    parsed['page'] = 1;

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/orders',
      search: stringified,
    });
  }

  function filterOrder(param, val) {
    if (val === '' || val === 0) {
      delete parsed[param];
    } else {
      parsed[param] = val;
    }
    parsed['page'] = 1;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/orders',
      search: stringified,
    });
  }

  function resetFilter() {
    delete parsed['courier'];
    delete parsed['endDate'];
    delete parsed['limit'];
    delete parsed['page'];
    delete parsed['q'];
    delete parsed['sortBy'];
    delete parsed['startDate'];
    delete parsed['status'];
    delete parsed['warehouse_id'];

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/orders',
      search: stringified,
    });
  }

  function onDowloadCSV() {
    fetchOrderForDownload(
      parsed['status'],
      parsed['q'],
      parsed['sortBy'],
      parsed['courier'],
      parsed['startDate'],
      parsed['endDate'],
      parsed['warehouse_id']
    );
  }

  useEffect(() => {
    getCourierList();
    getWarehouseList();
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* eslint-disable */
  useEffect(() => {
    if (parsed['q']) {
      setSearchValue(parsed['q']);
    } else {
      setSearchValue('');
    }

    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/orders',
          search: stringified,
        });
      } else {
        setPage(parsed['page']);
      }
    } else {
      setPage('1');
    }

    if (parsed['status']) {
      setStatus(parsed['status']);
    } else {
      setStatus('');
    }

    if (parsed['sortBy']) {
      setSortBy(parsed['sortBy']);
    } else {
      setSortBy('ordered_at desc');
    }

    if (parsed['courier']) {
      setSelectedCourier(parsed['courier']);
    } else {
      setSelectedCourier(0);
    }

    if (parsed['startDate']) {
      setStartDate(parsed['startDate']);
    } else {
      setStartDate('');
    }

    if (parsed['endDate']) {
      setEndDate(parsed['endDate']);
    } else {
      setEndDate('');
    }

    if (parsed['warehouse_id']) setSelectedWarehouse(parsed['warehouse_id']);
    else setSelectedWarehouse('');

    fetchOrderList({
      courier: parsed['courier'],
      endDate: parsed['endDate'],
      filter: parsed['status'],
      page: parsed['page'],
      query: parsed['q'],
      sort: parsed['sortBy'],
      startDate: parsed['startDate'],
      total: 10,
      warehouse_id: parsed['warehouse_id'],
    });
  }, [
    parsed['page'],
    parsed['q'],
    parsed['status'],
    parsed['sortBy'],
    parsed['courier'],
    parsed['startDate'],
    parsed['endDate'],
    parsed['warehouse_id'],
  ]);
  /* eslint-enable */

  const columsCsv = [
    {
      id: 'no',
      displayName: 'No',
    },
    {
      id: 'order_number',
      displayName: 'Order Number',
    },
    {
      id: 'invoice_number',
      displayName: 'Invoice Number',
    },
    {
      id: 'ordered_at',
      displayName: 'Ordered At',
    },
    {
      id: 'status',
      displayName: 'Status',
    },
    {
      id: 'warehouse',
      displayName: 'Warehouse',
    },
    {
      id: 'product_title',
      displayName: 'Product Title',
    },
    {
      id: 'quantity',
      displayName: 'Quantity',
    },
    {
      id: 'sku',
      displayName: 'SKU',
    },
    {
      id: 'price',
      displayName: 'Price',
    },
    {
      id: 'customer_name',
      displayName: 'Customer Name',
    },
    {
      id: 'customer_phone',
      displayName: 'Customer Phone',
    },
    {
      id: 'recipient',
      displayName: 'Recipient',
    },
    {
      id: 'recipient_phone',
      displayName: 'Recipient Phone',
    },
    {
      id: 'courier',
      displayName: 'Courier',
    },
    {
      id: 'shipping_cost',
      displayName: 'Shipping Cost',
    },
    {
      id: 'installation',
      displayName: 'Installation',
    },
    {
      id: 'installation_fee',
      displayName: 'Installation Fee',
    },
    {
      id: 'transport_fee',
      displayName: 'Transport Fee',
    },
    {
      id: 'subtotal',
      displayName: 'Subtotal',
    },
    {
      id: 'promo',
      displayName: 'Promo',
    },
    {
      id: 'total_payment',
      displayName: 'Total Payment',
    },
    {
      id: 'promo_code',
      displayName: 'Promo Code',
    },
  ];

  return (
    <Layout className="pd-cms-orders main-container">
      <div className="content">
        <div className="pageTitle">Orders</div>
        <Content className="pageContent">
          <Row gutter={18}>
            <Col span={14}>
              <Search
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                enterButton
                className="search-product"
                placeholder="Search Order List"
                onSearch={(val) => filterOrder('q', val)}
              />
            </Col>
            {orderForDownload.length > 0 ? (
              <Col span={4} offset={5}>
                <CsvDownloader
                  columns={columsCsv}
                  datas={orderForDownload}
                  filename="order-data-homestory"
                  wrapColumnChar='"'
                  separator=",">
                  <PdButton
                    className="csv-download-trigger"
                    type="primary"
                    style={{ height: 32, width: 150 }}
                    outline>
                    Download as CSV
                  </PdButton>
                </CsvDownloader>
              </Col>
            ) : (
              <Col span={4} offset={5} className="height-32">
                <PdButton
                  loading={loadOrderReport}
                  onClick={onDowloadCSV}
                  type="primary"
                  style={{ height: 32, width: 180 }}
                  outline>
                  Download as CSV
                </PdButton>
              </Col>
            )}
            <Col span={7} className="mt-1">
              <RangePicker
                className="w-full"
                format={dateFormat}
                onChange={onChangeDate}
                placeholder={['Start date', 'End date']}
                value={[
                  startDatePick && dayjs(startDatePick, dateFormat),
                  endDatePick && dayjs(endDatePick, dateFormat),
                ]}
              />
            </Col>
            <Col span={4} className="mt-1">
              <Select
                className="w-full"
                value={Number(selectedCourier)}
                onChange={filterOrder.bind(this, 'courier')}>
                <Option value={0}>All Couriers</Option>
                {couriers.map((courier, i) => (
                  <Option key={i} value={courier.id}>
                    {courier.title}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={4} className="mt-1">
              <Select
                className="w-full"
                value={Number(selectedWarehouse)}
                onChange={filterOrder.bind(this, 'warehouse_id')}>
                <Option value={0}>All Warehouse</Option>
                {warehouseList.map(({ id, title }) => (
                  <Option key={id} value={id}>
                    {title}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={3} className="mt-1">
              <Select
                value={sortBy}
                style={{ width: '100%' }}
                onChange={(val) => filterOrder('sortBy', val)}>
                <Option value="ordered_at desc">Latest</Option>
                <Option value="ordered_at asc">Oldest</Option>
                <Option value="status">Status</Option>
              </Select>
            </Col>
            <Col lg={{ span: 4, offset: 1 }} className="mt-1">
              <span className="reset-filter" onClick={resetFilter}>
                Reset Filter
              </span>
            </Col>

            <Col span={24}>
              <Row gutter={10}>
                <Col span={3} className="mt-2">
                  <span style={{ marginRight: '10px' }}>Filter by Status:</span>
                </Col>
                <Col span={19} className="mt-1">
                  <Row>
                    <StatusList
                      onChange={(val) => filterOrder('status', val)}
                      selectedStatus={status}
                      statusList={[
                        '',
                        'waiting_payment',
                        'payment_verified',
                        'processed',
                        'cancelled',
                        'rejected',
                        'failed',
                        'completed',
                      ]}
                    />
                  </Row>
                </Col>
              </Row>
            </Col>

            {!loadStatus && (
              <>
                <Col xs={24}>
                  <DataTable
                    columns={columns}
                    data={orderList}
                    progressPending={loadStatus}
                    noHeader
                    highlightOnHover
                    striped
                  />
                </Col>

                <Col span={8} offset={8}>
                  <div className="center-button">
                    {page !== '1' && (
                      <PdButton
                        type="primary"
                        outline
                        style={{ height: 32 }}
                        onClick={() => changePage(Number(page) - 1)}>
                        Previous
                      </PdButton>
                    )}
                    {nextPage !== -1 && (
                      <PdButton
                        type="primary"
                        outline
                        style={{ height: 32 }}
                        onClick={() => changePage(Number(page) + 1)}>
                        Next
                      </PdButton>
                    )}
                  </div>
                </Col>
              </>
            )}
          </Row>
        </Content>
      </div>
    </Layout>
  );
}

export default { Orders };
